import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { listCoreStatuses } from '@/lib/coreSystem';
import { pushNotification } from '@/lib/notifications';
import { buildWhatsAppUrl, cleanWhatsAppNumber } from '@/hooks/useStoreWhatsApp';
import { useThemePageTracking } from '@/hooks/useThemePageTracking';
import {
  getFeexPayActiveCountries,
  resolveFeexPayNetworks,
  type FeexPayConfig,
} from '@/lib/feexpay';
import { loadMerchantCheckoutConfig } from '@/lib/platformPayments';

/** Durée pendant laquelle un créneau non payé reste tenu (minutes). */
const HOLD_MINUTES = 15;

// ─── Types ───────────────────────────────────────────────────────────────────
interface BookingService {
  id: number;
  owner: number;
  service_name: string;
  duration: number;
  location: string;
  description: string;
  price?: number | null;
  currency?: string | null;
}

interface Availability {
  id: number;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

interface BreakSlot {
  id: number;
  day_of_week: string;
  start_time: string;
  end_time: string;
}

interface Holiday {
  id: number;
  date_from: string;
  date_to: string;
  title: string;
}

interface Appointment {
  id: number;
  date: string;
  time: string;
  status?: string | null;
  payment_status?: string | null;
  created_at?: string | null;
}

/**
 * Un rendez-vous bloque-t-il le créneau ?
 * Un paiement en cours ne le bloque que pendant quelques minutes :
 * passé ce délai, le créneau redevient disponible pour les autres visiteurs.
 */
function isHoldingSlot(appt: Appointment): boolean {
  const status = String(appt.status || '').toLowerCase();
  if (status === 'cancelled') return false;
  const paymentStatus = String(appt.payment_status || '').toLowerCase();
  if (paymentStatus !== 'pending' && paymentStatus !== 'unpaid') return true;
  const created = new Date(String(appt.created_at || '')).getTime();
  if (!created) return false;
  return Date.now() - created < HOLD_MINUTES * 60 * 1000;
}

interface OwnerInfo {
  name: string;
  email: string;
  nomcommerce: string;
  image: string;
  telephone?: string;
}

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const SHORT_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function toDateStr(d: Date) { return d.toISOString().split('T')[0]; }
function toTimeStr(d: Date) { return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; }
function parseTime(t: string) { const [h, m] = t.slice(0, 5).split(':').map(Number); return { h, m, total: h * 60 + m }; }
function formatDateFr(d: string) { return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
function addMinutes(timeStr: string, mins: number) {
  const total = parseTime(timeStr).total + mins;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
function timeOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string) {
  return parseTime(aStart).total < parseTime(bEnd).total && parseTime(aEnd).total > parseTime(bStart).total;
}

// ══════════════════════════════════════════════════════════════════════════════
export default function BookingPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendeurParam = Number(searchParams.get('vendeur')) || null;
  const [activeServiceId, setActiveServiceId] = useState<number | null>(serviceId ? Number(serviceId) : null);
  const [ownerServices, setOwnerServices] = useState<BookingService[]>([]);

  const [service, setService] = useState<BookingService | null>(null);
  const [owner, setOwner] = useState<OwnerInfo | null>(null);
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [breaks, setBreaks] = useState<BreakSlot[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  // Statuts métier du socle (portée « réservation ») pour les rendez-vous publics.
  const [, setReservationStatuses] = useState<{ code: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Calendar state
  const today = new Date();
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<'pick' | 'form'>('pick');

  // ── Form state
  const [formData, setFormData] = useState({ fullname: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  // ── Paiement Mobile Money (uniquement si le service est payant)
  const [payConfig, setPayConfig] = useState<FeexPayConfig | null>(null);
  const [payCountry, setPayCountry] = useState('');
  const [payNetwork, setPayNetwork] = useState('');
  const [payPhone, setPayPhone] = useState('');
  const [pendingPayment, setPendingPayment] = useState<{
    appointmentId: number;
    reference: string;
    amount: number;
    currency: string;
  } | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid' | 'failed'>('pending');

  // Suivi de visite de la page de réservation publique (vues, IP, géo, provenance).
  // On attend que le service soit résolu pour rattacher la visite à son commerce.
  useThemePageTracking('booking', service?.owner ?? null, !!service);

  // ── Fetch all data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        let svc: BookingService | null = null;
        let ownerKey: number | null = vendeurParam;

        // Service actif (route /booking/:serviceId)
        if (activeServiceId) {
          const { data, error: svcErr } = await supabase
            .from('booking_services')
            .select('*')
            .eq('id', activeServiceId)
            .eq('statut', 1)
            .maybeSingle();
          if (svcErr || !data) {
            if (!cancelled) { setError('Service introuvable ou désactivé'); setLoading(false); }
            return;
          }
          svc = data as BookingService;
          ownerKey = Number(svc.owner);
        }

        if (!ownerKey) {
          if (!cancelled) { setError('Aucun service à réserver pour le moment'); setLoading(false); }
          return;
        }
        if (cancelled) return;
        setService(svc);

        // Statuts de réservation définis dans le socle central (best-effort).
        if (svc) {
          try {
            const org = await getOrganizationByCommerce(svc.owner);
            if (org) {
              const statuses = await listCoreStatuses(org.id);
              if (!cancelled) {
                setReservationStatuses(statuses.filter((s) => s.scope === 'reservation').map((s) => ({ code: s.code, label: s.label })));
              }
            }
          } catch { /* best-effort : le socle peut être indisponible en public */ }
        }

        // Prestataire
        const { data: ownerData } = await supabase
          .from('users')
          .select('name, email, nomcommerce, image, telephone')
          .eq('id', ownerKey)
          .maybeSingle();
        if (!cancelled && ownerData) setOwner(ownerData as OwnerInfo);

        // Services de réservation du prestataire (sélection possible).
        const { data: svcList } = await supabase
          .from('booking_services')
          .select('*')
          .eq('owner', ownerKey)
          .eq('statut', 1)
          .order('service_name', { ascending: true });
        if (!cancelled) setOwnerServices((svcList as BookingService[] | null) || []);

        if (svc) {
          // Fetch availabilities, breaks, holidays, appointments (service-specific + global)
          const [avRes, brRes, hoRes, apRes, avGlobalRes, brGlobalRes, hoGlobalRes] = await Promise.all([
            supabase.from('booking_availabilities').select('*').eq('service_id', svc.id),
            supabase.from('booking_breaks').select('*').eq('service_id', svc.id),
            supabase.from('booking_holidays').select('*').eq('service_id', svc.id),
            supabase.from('booking_appointments').select('id, date, time, status, payment_status, created_at').eq('service_id', svc.id).neq('status', 'cancelled'),
            supabase.from('booking_availabilities').select('*').eq('service_id', 0),
            supabase.from('booking_breaks').select('*').eq('service_id', 0),
            supabase.from('booking_holidays').select('*').eq('service_id', 0),
          ]);
          if (cancelled) return;

          // Merge: per-service overrides global (by day_of_week for avail, by key for breaks/holidays)
          const mergedAvail = new Map<string, Availability>();
          (avGlobalRes.data || []).forEach((a: any) => mergedAvail.set(a.day_of_week, a));
          (avRes.data || []).forEach((a: any) => mergedAvail.set(a.day_of_week, a));

          const mergedBr = new Map<string, BreakSlot>();
          (brGlobalRes.data || []).forEach((b: any) => mergedBr.set(`${b.day_of_week}-${b.start_time}-${b.end_time}`, b));
          (brRes.data || []).forEach((b: any) => mergedBr.set(`${b.day_of_week}-${b.start_time}-${b.end_time}`, b));

          const mergedHol = new Map<string, Holiday>();
          (hoGlobalRes.data || []).forEach((h: any) => mergedHol.set(`${h.date_from}-${h.date_to}`, h));
          (hoRes.data || []).forEach((h: any) => mergedHol.set(`${h.date_from}-${h.date_to}`, h));

          setAvailabilities(Array.from(mergedAvail.values()) as Availability[]);
          setBreaks(Array.from(mergedBr.values()) as BreakSlot[]);
          setHolidays(Array.from(mergedHol.values()) as Holiday[]);
          setAppointments(((apRes.data || []) as Appointment[]).filter(isHoldingSlot));
        } else {
          setAvailabilities([]);
          setBreaks([]);
          setHolidays([]);
          setAppointments([]);
        }
      } catch {
        if (!cancelled) setError('Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeServiceId, vendeurParam]);

  // S'il n'y a qu'un seul service de réservation, on le sélectionne directement.
  useEffect(() => {
    if (!activeServiceId && ownerServices.length === 1) setActiveServiceId(ownerServices[0].id);
  }, [ownerServices, activeServiceId]);

  // Réinitialise la sélection de créneau quand on change de service.
  useEffect(() => {
    setSelectedDate(null);
    setSelectedTime(null);
    setCurrentStep('pick');
  }, [activeServiceId]);

  const servicePrice = Number(service?.price || 0);
  const serviceCurrency = service?.currency || 'MAD';

  // Le paiement en ligne n'est proposé que si le commerce y est autorisé.
  // Sinon, la réservation fonctionne comme avant : règlement sur place.
  const [merchantCanPayOnline, setMerchantCanPayOnline] = useState(false);
  useEffect(() => {
    if (!service?.owner) { setMerchantCanPayOnline(false); return; }
    let active = true;
    void loadMerchantCheckoutConfig(Number(service.owner)).then((cfg) => {
      if (active) setMerchantCanPayOnline(cfg.onlineEnabled);
    });
    return () => { active = false; };
  }, [service?.owner]);

  const requiresOnlinePayment = servicePrice > 0 && merchantCanPayOnline;

  // Configuration Mobile Money (pays / opérateurs activés par le SuperAdmin).
  useEffect(() => {
    if (!service || !requiresOnlinePayment) return;
    let active = true;
    void (async () => {
      const { data } = await supabase
        .from('payment_providers')
        .select('config, enabled')
        .eq('provider', 'feexpay')
        .maybeSingle();
      if (!active) return;
      const cfg = ((data?.config || {}) as FeexPayConfig) || null;
      setPayConfig(cfg);
      const countries = getFeexPayActiveCountries(cfg);
      const firstCountry = String(cfg?.country || '') || countries[0]?.code || '';
      setPayCountry((prev) => prev || firstCountry);
      const nets = resolveFeexPayNetworks(cfg).filter((o) => o.enabled && o.country === firstCountry);
      const defaultNet = String(cfg?.network || '');
      setPayNetwork((prev) => prev || (nets.some((o) => o.slug === defaultNet) ? defaultNet : nets[0]?.slug || ''));
    })();
    return () => { active = false; };
  }, [service, requiresOnlinePayment]);

  // Suivi du paiement : le créneau n'est confirmé qu'après encaissement.
  useEffect(() => {
    if (!pendingPayment) return;
    let cancelled = false;
    const check = async () => {
      const { data } = await supabase
        .from('booking_appointments')
        .select('payment_status')
        .eq('id', pendingPayment.appointmentId)
        .maybeSingle();
      if (cancelled) return;
      const status = String((data as { payment_status?: string } | null)?.payment_status || 'pending').toLowerCase();
      if (status === 'paid') {
        setPaymentStatus('paid');
        setSuccess(true);
        setPendingPayment(null);
        void pushNotification({
          userId: service?.owner || 0,
          type: 'booking',
          message: `Réservation payée : ${service?.service_name}, ${selectedDate} à ${selectedTime}.`,
        });
      } else if (status === 'failed') {
        setPaymentStatus('failed');
      }
    };
    void check();
    const timer = window.setInterval(() => { void check(); }, 6000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [pendingPayment, service, selectedDate, selectedTime]);

  // ── Calendar helpers
  const firstOfMonth = new Date(calYear, calMonth, 1);
  const lastOfMonth = new Date(calYear, calMonth + 1, 0);
  const startPad = firstOfMonth.getDay() === 0 ? 6 : firstOfMonth.getDay() - 1;
  const monthDays: (number | null)[] = [];
  for (let i = 0; i < startPad; i++) monthDays.push(null);
  for (let i = 1; i <= lastOfMonth.getDate(); i++) monthDays.push(i);

  const isHoliday = (dateStr: string) => holidays.some((h) => dateStr >= h.date_from && dateStr <= h.date_to);
  const isWorkingDay = (dateStr: string) => {
    const d = new Date(dateStr);
    const dayKey = DAY_KEYS[(d.getDay() + 6) % 7];
    return availabilities.some((a) => a.day_of_week === dayKey);
  };
  const isDateAvailable = (dateStr: string) => {
    if (!service) return false;
    if (dateStr < toDateStr(today)) return false;
    if (isHoliday(dateStr)) return false;
    if (!isWorkingDay(dateStr)) return false;
    // Check if at least one slot is available
    return getAvailableSlots(dateStr).length > 0;
  };

  // ── Time slot generation
  const getAvailableSlots = useCallback((dateStr: string): string[] => {
    if (!service) return [];
    const d = new Date(dateStr);
    const dayKey = DAY_KEYS[(d.getDay() + 6) % 7];
    const avail = availabilities.find((a) => a.day_of_week === dayKey);
    if (!avail) return [];

    const dayBreaks = breaks.filter((b) => b.day_of_week === dayKey);
    const dayAppts = appointments.filter((a) => a.date === dateStr);
    const now = new Date();
    const isToday = dateStr === toDateStr(now);
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const slots: string[] = [];
    const startTotal = parseTime(avail.start_time).total;
    const endTotal = parseTime(avail.end_time).total;

    for (let t = startTotal; t + service.duration <= endTotal; t += service.duration) {
      const slotStart = `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
      const slotEnd = addMinutes(slotStart, service.duration);

      // Skip past slots
      if (isToday && t <= currentMinutes) continue;

      // Skip if overlaps with any break
      const overlapsBreak = dayBreaks.some((b) => timeOverlap(slotStart, slotEnd, b.start_time, b.end_time));
      if (overlapsBreak) continue;

      // Skip if overlaps with existing appointment
      const overlapsAppt = dayAppts.some((a) => timeOverlap(slotStart, slotEnd, a.time, addMinutes(a.time, service.duration)));
      if (overlapsAppt) continue;

      slots.push(slotStart);
    }
    return slots;
  }, [service, availabilities, breaks, appointments]);

  // ── Available slots for selected date
  const availableSlots = selectedDate ? getAvailableSlots(selectedDate) : [];

  // Contact du prestataire (Appeler / WhatsApp), affiché en permanence.
  const ownerName = owner?.nomcommerce || owner?.name || 'Réservation';
  const ownerPhone = (owner?.telephone || '').trim();
  const ownerWaUrl = ownerPhone
    ? buildWhatsAppUrl(
        cleanWhatsAppNumber(ownerPhone),
        `Bonjour ${ownerName}, je vous contacte au sujet de ${service?.service_name || 'votre service'}.`,
      )
    : '';

  // Filtre de disponibilité : on tient compte des RDV payés et des paiements
  // encore en cours (hold de quelques minutes).
  // ── Submit booking
  const handleSubmit = async () => {
    if (!service || !selectedDate || !selectedTime) return;
    if (!formData.fullname.trim()) { setFormError('Le nom est requis'); return; }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { setFormError('Email valide requis'); return; }
    if (requiresOnlinePayment) {
      if (!payCountry || !payNetwork) { setFormError('Choisissez votre pays et votre opérateur Mobile Money.'); return; }
      if (payPhone.trim().length < 8) { setFormError('Indiquez le numéro Mobile Money qui sera débité.'); return; }
    }

    setSubmitting(true);
    setFormError('');
    setPaymentStatus('pending');

    try {
      const { data, error: fnError } = await supabase.functions.invoke('zcal-booking-checkout', {
        body: {
          serviceId: service.id,
          date: selectedDate,
          time: selectedTime,
          fullname: formData.fullname.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          message: formData.message.trim(),
          country: payCountry,
          network: payNetwork,
          phoneNumber: payPhone,
        },
      });

      if (fnError) {
        let message = 'La réservation n’a pas pu être enregistrée.';
        const ctx = (fnError as { context?: { json?: () => Promise<{ error?: string }> } }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = await ctx.json();
            if (parsed?.error) message = parsed.error;
          } catch { /* message par défaut */ }
        }
        setFormError(message);
        setSubmitting(false);
        return;
      }

      const res = (data || {}) as {
        free?: boolean;
        pending?: boolean;
        appointmentId?: number;
        reference?: string;
        amount?: number;
        currency?: string;
      };

      if (res.pending && res.appointmentId) {
        // Paiement Mobile Money : le créneau n'est PAS encore bloqué.
        setPendingPayment({
          appointmentId: Number(res.appointmentId),
          reference: String(res.reference || ''),
          amount: Number(res.amount) || servicePrice,
          currency: String(res.currency || serviceCurrency),
        });
        setSubmitting(false);
        return;
      }

      // Réservation gratuite : confirmée immédiatement.
      void pushNotification({
        userId: service.owner,
        type: 'booking',
        message: `Nouvelle réservation : ${service.service_name}, le ${selectedDate} à ${selectedTime} (${formData.fullname.trim()}).`,
      });

      if (owner?.email) {
        supabase.functions.invoke('send-booking-notification', {
          body: {
            to: owner.email,
            type: 'new_booking',
            service_name: service.service_name,
            customer_name: formData.fullname.trim(),
            customer_email: formData.email.trim(),
            customer_phone: formData.phone.trim(),
            customer_message: formData.message.trim(),
            date: selectedDate,
            time: selectedTime,
            duration: service.duration,
          },
        }).catch(() => { /* silencieux : l'email ne bloque pas la réservation */ });
      }

      setSuccess(true);
    } catch {
      setFormError('Une erreur est survenue. Veuillez réessayer.');
    }
    setSubmitting(false);
  };

  // ── Loading / Error states
  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-2xl text-red-500"></i>
        </div>
        <h2 className="text-lg font-semibold text-foreground-900 mb-2">Service indisponible</h2>
        <p className="text-sm text-foreground-500 mb-6">{error}</p>
        <button onClick={() => navigate('/')} className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">
          Retour à l'accueil
        </button>
      </div>
    );
  }

  // ── Paiement Mobile Money en cours (créneau non encore bloqué)
  if (pendingPayment) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-5 ${
          paymentStatus === 'failed' ? 'bg-red-50' : 'bg-secondary-100'
        }`}>
          <i className={`text-3xl ${
            paymentStatus === 'failed' ? 'ri-close-line text-red-500' : 'ri-smartphone-line text-secondary-700'
          }`}></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2 text-center">
          {paymentStatus === 'failed' ? 'Paiement refusé' : 'Paiement en cours'}
        </h2>
        <p className="text-sm text-foreground-500 text-center max-w-md mb-2 leading-relaxed">
          {paymentStatus === 'failed'
            ? 'Le paiement n’a pas abouti. Votre créneau n’a pas été bloqué, vous pouvez réessayer.'
            : `Une demande de ${pendingPayment.amount.toLocaleString('fr-FR')} ${pendingPayment.currency} a été envoyée au ${payPhone}. Validez-la sur votre téléphone : votre créneau sera bloqué dès la confirmation.`}
        </p>
        <p className="text-xs text-foreground-400 text-center max-w-md mb-6">
          {formatDateFr(selectedDate || '')} à {selectedTime} · {service?.service_name}
        </p>

        {pendingPayment.reference && (
          <div className="px-3 py-2 bg-background-100/70 rounded-lg text-[11px] text-foreground-600 mb-5">
            Référence : <span className="font-mono text-foreground-800 break-all">{pendingPayment.reference}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-2">
          {paymentStatus !== 'failed' && (
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium">
              <i className="ri-loader-4-line animate-spin"></i>
              En attente de la confirmation de l’opérateur…
            </span>
          )}
          <button
            type="button"
            onClick={() => { setPendingPayment(null); setPaymentStatus('pending'); }}
            className="px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            {paymentStatus === 'failed' ? 'Réessayer' : 'Annuler et revenir'}
          </button>
        </div>

        <p className="text-[11px] text-foreground-400 text-center max-w-md mt-6 leading-relaxed">
          Le créneau reste réservé pendant {HOLD_MINUTES} minutes le temps du paiement. Passé ce délai sans confirmation, il redevient disponible.
        </p>
      </div>
    );
  }

  // ── Success
  if (success) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mb-5">
          <i className="ri-check-line text-3xl text-emerald-500"></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2">Rendez-vous confirmé !</h2>
        <p className="text-sm text-foreground-500 text-center max-w-md mb-2">
          {formatDateFr(selectedDate!)} à {selectedTime}
        </p>
        <p className="text-xs text-foreground-400 text-center max-w-md mb-8">
          {service?.service_name} ({service?.duration} min) — Un email de confirmation sera envoyé à {formData.email}
        </p>        <button onClick={() => navigate('/')} className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap">
          Retour à l'accueil
        </button>
      </div>
    );
  }

  // ── Sélection du service à réserver (arrivée depuis la boutique du vendeur)
  if (!service) {
    return (
      <div className="min-h-screen bg-background-50">
        <header className="border-b border-background-200/70 bg-background-50">
          <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-background-100 border border-background-200/70 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {owner?.image ? (
                  <img
                    src={`https://seaweedfs-oriq.srv1134875.hstgr.cloud/product-media/${owner.image}`}
                    alt={ownerName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-primary-600 font-bold text-base font-heading">{ownerName.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div>
                <h1 className="text-base font-bold font-heading text-foreground-950 leading-tight">{ownerName}</h1>
                <p className="text-xs text-foreground-500">Choisissez un service à réserver</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {ownerWaUrl && (
                <a
                  href={ownerWaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-green-500 text-white text-sm font-semibold no-underline hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i>
                  <span className="hidden sm:inline">WhatsApp</span>
                </a>
              )}
              {ownerPhone && (
                <a
                  href={`tel:${ownerPhone}`}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i>
                  <span className="hidden sm:inline">Appeler</span>
                </a>
              )}
            </div>
          </div>
        </header>

        <div className="w-full max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2 text-center">
            Réserver un rendez-vous
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            Sélectionnez le service qui vous intéresse.
          </p>

          {ownerServices.length === 0 ? (
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-10 text-center">
              <i className="ri-calendar-close-line text-3xl text-foreground-300 mb-3 block"></i>
              <p className="text-sm text-foreground-500">Aucun service de réservation n&apos;est disponible pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ownerServices.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveServiceId(s.id)}
                  className="text-left bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-accent-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-check-line text-lg text-accent-600"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900">{s.service_name}</h3>
                    {Number(s.price || 0) > 0 && (
                      <p className="text-xs font-semibold text-accent-700 mt-0.5">
                        {Number(s.price).toLocaleString('fr-FR')} {s.currency || 'MAD'}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5 text-xs text-foreground-500">
                    <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>{s.duration} minutes</span>
                    {s.location && <span className="flex items-center gap-1.5"><i className="ri-map-pin-line"></i>{s.location}</span>}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
                    Choisir ce service <i className="ri-arrow-right-line"></i>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <header className="border-b border-background-200/70 bg-background-50">
        <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Logo */}
            <div className="w-11 h-11 rounded-xl bg-background-100 border border-background-200/70 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {owner?.image ? (
                <img
                  src={`https://seaweedfs-oriq.srv1134875.hstgr.cloud/product-media/${owner.image}`}
                  alt={owner?.nomcommerce || 'Logo'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).parentElement!.classList.add('bg-primary-50');
                    const fallback = document.createElement('span');
                    fallback.className = 'text-primary-600 font-bold text-base font-heading';
                    fallback.textContent = (owner?.nomcommerce || 'R')[0].toUpperCase();
                    (e.target as HTMLImageElement).parentElement!.appendChild(fallback);
                  }}
                />
              ) : (
                <span className="text-primary-600 font-bold text-base font-heading">
                  {(owner?.nomcommerce || 'R')[0].toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-base font-bold font-heading text-foreground-950 leading-tight">
                {owner?.nomcommerce || owner?.name || 'Réservation'}
              </h1>
              <p className="text-xs text-foreground-500">{service?.service_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {ownerServices.length > 1 && (
              <select
                value={activeServiceId ?? ''}
                onChange={(e) => setActiveServiceId(Number(e.target.value))}
                className="hidden md:block px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-800 border border-background-200/70 focus:outline-none focus:border-primary-300 cursor-pointer max-w-[200px]"
              >
                {ownerServices.map((s) => (
                  <option key={s.id} value={s.id}>{s.service_name}</option>
                ))}
              </select>
            )}
            <span className="px-3 py-1 bg-accent-50 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">{service?.duration} min</span>
            {service?.location && (
              <span className="text-xs text-foreground-500 hidden lg:flex items-center gap-1">
                <i className="ri-map-pin-line"></i>{service.location}
              </span>
            )}
            {ownerPhone && (
              <a
                href={`tel:${ownerPhone}`}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                <span className="hidden sm:inline">Appeler</span>
              </a>
            )}
            {ownerWaUrl && (
              <a
                href={ownerWaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500 text-white text-sm font-semibold no-underline hover:bg-green-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                <span className="hidden sm:inline">WhatsApp</span>
              </a>
            )}
          </div>
        </div>
      </header>

      <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
          {/* Left: Calendar + Time slots */}
          <div className="lg:col-span-3">
            {currentStep === 'pick' && (
              <>
                {/* Calendar */}
                <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden mb-6">
                  <div className="flex items-center justify-between p-4 border-b border-background-200/70">
                    <button
                      onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200/70 cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-left-s-line text-foreground-600"></i>
                    </button>
                    <h3 className="text-sm font-semibold text-foreground-800">{MONTHS[calMonth]} {calYear}</h3>
                    <button
                      onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200/70 cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-right-s-line text-foreground-600"></i>
                    </button>
                  </div>

                  {/* Day headers */}
                  <div className="grid grid-cols-7 border-b border-background-200/70">
                    {SHORT_DAYS.map((d) => (
                      <div key={d} className="py-2 text-center text-[11px] font-medium text-foreground-400">{d}</div>
                    ))}
                  </div>

                  {/* Days */}
                  <div className="grid grid-cols-7 p-2 gap-1">
                    {monthDays.map((day, i) => {
                      if (day === null) return <div key={`e-${i}`} className="aspect-square"></div>;
                      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const isToday = dateStr === toDateStr(today);
                      const available = isDateAvailable(dateStr);
                      const isSelected = dateStr === selectedDate;
                      const hol = holidays.find((h) => dateStr >= h.date_from && dateStr <= h.date_to);

                      return (
                        <button
                          key={dateStr}
                          disabled={!available}
                          onClick={() => { setSelectedDate(dateStr); setSelectedTime(null); }}
                          className={`aspect-square flex flex-col items-center justify-center rounded-lg text-xs cursor-pointer transition-all ${
                            isSelected ? 'bg-primary-500 text-background-50 font-bold' :
                            isToday ? 'bg-primary-50 text-primary-700 font-semibold ring-1 ring-primary-200' :
                            available ? 'bg-background-50 text-foreground-700 hover:bg-accent-50 hover:text-accent-700' :
                            'bg-background-100/50 text-foreground-300 cursor-not-allowed'
                          }`}
                        >
                          <span>{day}</span>
                          {hol && <span className="text-[8px] opacity-60">{hol.title.slice(0, 10)}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time slots */}
                {selectedDate && (
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-3">
                      <i className="ri-time-line mr-1.5 text-primary-500"></i>
                      {formatDateFr(selectedDate)}
                    </h3>
                    {availableSlots.length === 0 ? (
                      <div className="bg-background-50 border border-background-200/70 rounded-xl p-8 text-center">
                        <i className="ri-calendar-close-line text-3xl text-foreground-300 mb-2 block"></i>
                        <p className="text-sm text-foreground-500">Aucun créneau disponible à cette date</p>
                        <p className="text-xs text-foreground-400 mt-1">Choisissez une autre date</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            onClick={() => setSelectedTime(slot)}
                            className={`py-2.5 px-3 rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                              selectedTime === slot
                                ? 'bg-primary-500 text-background-50'
                                : 'bg-background-50 border border-background-200/70 text-foreground-700 hover:border-accent-300 hover:text-accent-700'
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    )}

                    {selectedTime && (
                      <button
                        onClick={() => setCurrentStep('form')}
                        className="mt-5 w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        Continuer <i className="ri-arrow-right-line ml-1"></i>
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Step 2: Form */}
            {currentStep === 'form' && (
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                <button
                  onClick={() => setCurrentStep('pick')}
                  className="flex items-center gap-1 text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer mb-4 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i> Retour
                </button>
                <h3 className="text-base font-semibold text-foreground-900 mb-1">Vos informations</h3>
                <p className="text-xs text-foreground-500 mb-5">
                  {formatDateFr(selectedDate!)} à {selectedTime} · {service?.service_name} ({service?.duration} min)
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Nom complet *</label>
                    <input
                      type="text"
                      value={formData.fullname}
                      onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
                      placeholder="Votre nom"
                      className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="votre@email.com"
                      className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+33 6 12 34 56 78"
                      className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Message (optionnel)</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Précisez l'objet du rendez-vous..."
                      rows={3}
                      maxLength={500}
                      className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                    />
                    <p className="text-[10px] text-foreground-400 text-right mt-0.5">{formData.message.length}/500</p>
                  </div>

                  {servicePrice > 0 && (
                    <div className="rounded-lg border border-accent-200/70 bg-accent-50/60 p-3">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-xs font-medium text-accent-800 flex items-center gap-1.5">
                          <i className={requiresOnlinePayment ? 'ri-secure-payment-line' : 'ri-hand-coin-line'}></i>
                          {requiresOnlinePayment ? 'Paiement Mobile Money requis' : 'Paiement sur place'}
                        </span>
                        <span className="text-base font-bold font-heading text-accent-900 whitespace-nowrap">
                          {servicePrice.toLocaleString('fr-FR')} {serviceCurrency}
                        </span>
                      </div>
                      <p className="text-[11px] text-accent-700 leading-relaxed">
                        {requiresOnlinePayment
                          ? 'Votre créneau est bloqué uniquement après confirmation du paiement.'
                          : 'Le paiement en ligne n’est pas activé pour ce prestataire : votre créneau est réservé et le règlement se fait sur place.'}
                      </p>
                    </div>
                  )}

                  {requiresOnlinePayment && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">Pays</label>
                          <select
                            value={payCountry}
                            onChange={(e) => {
                              const c = e.target.value;
                              setPayCountry(c);
                              const first = resolveFeexPayNetworks(payConfig).find((o) => o.enabled && o.country === c);
                              setPayNetwork(first?.slug || '');
                            }}
                            className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                          >
                            {getFeexPayActiveCountries(payConfig).map((c) => (
                              <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">Opérateur</label>
                          <select
                            value={payNetwork}
                            onChange={(e) => setPayNetwork(e.target.value)}
                            className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                          >
                            {resolveFeexPayNetworks(payConfig).filter((o) => o.enabled && o.country === payCountry).map((o) => (
                              <option key={`${o.country}-${o.slug}`} value={o.slug}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">Numéro Mobile Money à débiter *</label>
                        <input
                          type="tel"
                          value={payPhone}
                          onChange={(e) => setPayPhone(e.target.value)}
                          placeholder="Ex. 01 23 45 67 89"
                          className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-lg bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
                        />
                      </div>
                    </div>
                  )}

                  {formError && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>Réservation en cours...
                      </span>
                    ) : requiresOnlinePayment ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-secure-payment-line"></i>
                        Payer {servicePrice.toLocaleString('fr-FR')} {serviceCurrency} et réserver
                      </span>
                    ) : (
                      'Confirmer la réservation'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: Service details */}
          <div className="lg:col-span-2">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 sticky top-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-check-line text-lg text-accent-600"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-900">{service?.service_name}</h4>
                  <p className="text-xs text-foreground-500">{service?.duration} minutes</p>
                </div>
              </div>

              {service?.description && (
                <p className="text-xs text-foreground-600 mb-4 leading-relaxed">{service.description}</p>
              )}

              <div className="space-y-2.5 text-xs text-foreground-600">
                {service?.location && (
                  <div className="flex items-start gap-2">
                    <i className="ri-map-pin-line mt-0.5 text-foreground-400"></i>
                    <span>{service.location}</span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <i className="ri-time-line mt-0.5 text-foreground-400"></i>
                  <span>{service?.duration} minutes</span>
                </div>
                {owner?.nomcommerce && (
                  <div className="flex items-start gap-2">
                    <i className="ri-store-2-line mt-0.5 text-foreground-400"></i>
                    <span>{owner.nomcommerce}</span>
                  </div>
                )}
              </div>

              {/* Selected slot preview */}
              {selectedDate && selectedTime && (
                <div className="mt-4 pt-4 border-t border-background-200/70">
                  <p className="text-[10px] font-medium text-foreground-400 uppercase tracking-wide mb-2">Créneau sélectionné</p>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-event-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{selectedTime}</p>
                      <p className="text-[10px] text-foreground-500">{formatDateFr(selectedDate)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}